import React, { useState, useEffect } from "react";
import { createProduct } from "../services/productService";
import { getCategoryTree } from "../services/categoryService"; // changed import
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import "bootstrap/dist/css/bootstrap.min.css";
import { FaPlusCircle, FaMagic } from "react-icons/fa";
import { getBrands } from "../services/brandService"; // added import
import { getManufacturers } from "../services/manufacturerService"; // added import

const AddProduct = () => {
  const navigate = useNavigate();
  const [product, setProduct] = useState({
    name: "",
    sku: "",
    categoryId: [], // updated to array for multiple categories
    price: "",
    mrp: "",
    specialPrice: "",
    description: "",
    shortDescription: "",
    slug: "",
    metaTitle: "",
    metaDescription: "",
    metaKeywords: "",
    canonicalUrl: "",
    imageAltText: "",
    imageUrl: "",
    brand: "",
    brandName: "",
    manufacturerCode: "",
    manufacturerName: "",
    genericName: "",
    countryOfOrigin: "",
    moq: "",
    schemaMarkupJson: "",
    hsnCode: "",         // new field for HSN Code
    enableDisable: 1,    // new field for Status (1 = Enable, 0 = Disable)
  });

  const [brands, setBrands] = useState([]);
  const [manufacturers, setManufacturers] = useState([]);
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const data = await getCategoryTree(); // use category tree endpoint
        setCategories(data.data || []);
      } catch (error) {
        toast.error("Error fetching category tree.");
      }
    };
    fetchCategories();
  }, []);

  // NEW: Fetch Brands
  useEffect(() => {
    const fetchBrands = async () => {
      try {
        const res = await getBrands();
        setBrands(res.data.content || []);
      } catch (error) {
        toast.error("Error fetching brands.");
      }
    };
    fetchBrands();
  }, []);
  
  // NEW: Fetch Manufacturers
  useEffect(() => {
    const fetchManufacturers = async () => {
      try {
        const res = await getManufacturers();
        setManufacturers(res.data.content || []);
      } catch (error) {
        toast.error("Error fetching manufacturers.");
      }
    };
    fetchManufacturers();
  }, []);

  const handleChange = (e) => {
    setProduct({ ...product, [e.target.name]: e.target.value });
  };

  // Auto-generate SEO fields based on Product Name
  const generateSEO = () => {
    if (product.name) {
      const slug = product.name.toLowerCase().replace(/ /g, "-").replace(/[^a-z0-9-]/g, "");
      const metaTitle = `${product.name} - Buy Now at Best Price`;
      const metaDescription = `Get the best deal on ${product.name}. Available online at the best price.`;
      const metaKeywords = product.name.toLowerCase().split(" ").join(", ");
      const canonicalUrl = `https://medicine.shop/products/${slug}`;
      const imageAltText = `${product.name} product image`;

      setProduct({ ...product, slug, metaTitle, metaDescription, metaKeywords, canonicalUrl, imageAltText });
      toast.success("SEO fields auto-generated! ✨");
    } else {
      toast.warning("Enter product name first! ⚠️");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // Transform product state to match the payload format
      const payload = {
        ...product,
        // Map categoryId array to categoryIds key
        categoryIds: product.categoryId,
        // Optionally convert numeric fields
        price: parseFloat(product.price),
        mrp: parseFloat(product.mrp),
        specialPrice: parseFloat(product.specialPrice),
      };
      await createProduct(payload);
      toast.success("Product added successfully! ✅");
      navigate("/products");
    } catch (err) {
      toast.error("Failed to add product! ❌");
    }
  };

  // Handler for checkbox changes
  const handleCheckboxChange = (e, id) => {
    const checked = e.target.checked;
    let newSelected;
    if (checked) {
      newSelected = [...product.categoryId, String(id)];
    } else {
      newSelected = product.categoryId.filter((catId) => catId !== String(id));
    }
    setProduct({ ...product, categoryId: newSelected });
  };

  // Recursively render checkboxes for category tree with indentation
  const renderCategoryCheckboxes = (categories, level = 0) => {
    return categories.map((cat) => (
      <div key={cat.entityId} className="form-check" style={{ marginLeft: level * 20 + "px" }}>
        <input
          className="form-check-input"
          type="checkbox"
          value={cat.entityId}
          id={`cat-${cat.entityId}`}
          checked={product.categoryId.includes(String(cat.entityId))}
          onChange={(e) => handleCheckboxChange(e, cat.entityId)}
        />
        <label className="form-check-label" htmlFor={`cat-${cat.entityId}`}>
          {cat.categoryName}
        </label>
        {cat.subcategories && cat.subcategories.length > 0 && renderCategoryCheckboxes(cat.subcategories, level + 1)}
      </div>
    ));
  };

  return (
    <div className="container-fluid px-5 py-4 bg-light">
      <div className="card shadow-lg p-5 mb-5" style={{ borderRadius: "20px" }}>
        <div className="card-header bg-primary text-white text-center" style={{ borderRadius: "20px 20px 0 0" }}>
          <h2><FaPlusCircle className="me-2" /> Add New Product</h2>
        </div>
        <div className="card-body">
          <form onSubmit={handleSubmit} className="row g-4">
            {/* Product Details */}
            <h5 className="text-primary border-bottom pb-2">🛍️ Product Details</h5>
            <div className="col-md-6">
              <label className="form-label">Product Name</label>
              <input type="text" className="form-control" name="name" value={product.name} onChange={handleChange} required />
            </div>
            <div className="col-md-6">
              <label className="form-label">SKU</label>
              <input type="text" className="form-control" name="sku" value={product.sku} onChange={handleChange} required />
            </div>
            <div className="col-md-6">
              <label className="form-label">Categories</label>
              <div className="border p-2" style={{ maxHeight: "200px", overflowY: "auto" }}>
                {renderCategoryCheckboxes(categories)}
              </div>
            </div>
            <div className="col-md-4">
              <label className="form-label">Price (₹)</label>
              <input type="number" className="form-control" name="price" value={product.price} onChange={handleChange} required />
            </div>
            <div className="col-md-4">
              <label className="form-label">MRP (₹)</label>
              <input type="number" className="form-control" name="mrp" value={product.mrp} onChange={handleChange} />
            </div>
            <div className="col-md-6">
              <label className="form-label">HSN Code</label>
              <input
                type="text"
                className="form-control"
                name="hsnCode"
                value={product.hsnCode}
                onChange={handleChange}
                placeholder="Enter HSN Code"
              />
            </div>
            <div className="col-md-6">
              <label className="form-label">Status</label>
              <select
                className="form-select"
                name="enableDisable"
                value={product.enableDisable}
                onChange={handleChange}
              >
                <option value={1}>Enable</option>
                <option value={0}>Disable</option>
              </select>
            </div>
            {/* New field: Tax Percent dropdown */}
            <div className="col-md-6">
              <label className="form-label">Tax Percent (%)</label>
              <select
                className="form-select"
                name="taxPercent"
                value={product.taxPercent || "0"}
                onChange={handleChange}
              >
                <option value="0">0</option>
                <option value="5">5</option>
                <option value="12">12</option>
                <option value="18">18</option>
              </select>
            </div>
            <div className="col-md-6">
              <label className="form-label">Brand</label>
              <input
                type="text"
                className="form-control"
                name="brand"
                value={product.brand}
                onChange={handleChange}
                list="brandOptions"
              />
              <datalist id="brandOptions">
                {brands.map((b) => (
                  <option key={b.id} value={b.name} />
                ))}
              </datalist>
            </div>
            <div className="col-md-6">
              <label className="form-label">Manufacturer</label>
              <input
                type="text"
                className="form-control"
                name="manufacturerName"
                value={product.manufacturerName}
                onChange={handleChange}
                list="manufacturerOptions"
              />
              <datalist id="manufacturerOptions">
                {manufacturers.map((m) => (
                  <option key={m.id} value={m.manufacturerName} />
                ))}
              </datalist>
            </div>
            {/* Product Descriptions */}
            <h5 className="text-primary border-bottom pb-2">📝 Descriptions</h5>
            <div className="col-12">
              <label className="form-label">Short Description</label>
              <input type="text" className="form-control" name="shortDescription" value={product.shortDescription} onChange={handleChange} />
            </div>
            <div className="col-12">
              <label className="form-label">Full Description</label>
              <textarea className="form-control" rows="4" name="description" value={product.description} onChange={handleChange}></textarea>
            </div>

            

            {/* Additional Details */}
            <h5 className="text-primary border-bottom pb-2">📌 Additional Information</h5>
            <div className="col-md-4">
              <label className="form-label">Country of Origin</label>
              <input type="text" className="form-control" name="countryOfOrigin" value={product.countryOfOrigin} onChange={handleChange} />
            </div>
            
            <div className="col-md-4">
              <label className="form-label">Minimum Order Quantity (MOQ)</label>
              <input type="number" className="form-control" name="moq" value={product.moq} onChange={handleChange} />
            </div>
            
            
            {/* SEO Section */}
            <h5 className="text-primary border-bottom pb-2 d-flex justify-content-between align-items-center">
              📈 SEO Details 
              <button type="button" className="btn btn-sm btn-success" onClick={generateSEO}>
                <FaMagic /> Auto Generate
              </button>
            </h5>

            <div className="col-md-4">
              <label className="form-label">Slug</label>
              <input type="text" className="form-control" name="slug" value={product.slug} onChange={handleChange} required />
            </div>
            <div className="col-md-4">
              <label className="form-label">Meta Title</label>
              <input type="text" className="form-control" name="metaTitle" value={product.metaTitle} onChange={handleChange} />
            </div>
            <div className="col-md-4">
              <label className="form-label">Meta Description</label>
              <input type="text" className="form-control" name="metaDescription" value={product.metaDescription} onChange={handleChange} />
            </div>
            <div className="col-md-6">
              <label className="form-label">Meta Keywords</label>
              <input type="text" className="form-control" name="metaKeywords" value={product.metaKeywords} onChange={handleChange} />
            </div>
            <div className="col-md-6">
              <label className="form-label">Canonical URL</label>
              <input type="text" className="form-control" name="canonicalUrl" value={product.canonicalUrl} onChange={handleChange} />
            </div>
            <div className="col-md-6">
              <label className="form-label">Image Alt Text</label>
              <input type="text" className="form-control" name="imageAltText" value={product.imageAltText} onChange={handleChange} />
            </div>

            {/* Submit Buttons */}
            <div className="col-12 text-center mt-4">
              <button type="submit" className="btn btn-lg btn-primary">✅ Add Product</button>
              <button type="button" className="btn btn-lg btn-secondary ms-3" onClick={() => navigate("/products")}>❌ Cancel</button>
            </div>

          </form>
        </div>
      </div>
    </div>
  );
};

export default AddProduct;
